import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../Icon/Icon";
import clsx from "clsx";
import classes from "./Select.module.scss";

export const Select: FC = () => {
	const { i18n } = useTranslation();
	const [selectedOption, setSelectedOption] = useState<string>(() => {
		const storedLanguage = localStorage.getItem("language");
		return storedLanguage || i18n.language;
	});
	const [isOpen, setIsOpen] = useState(false);
	const selectRef = useRef<HTMLDivElement>(null);
	const [isRussian, setIsRussian] = useState<boolean>(i18n.language === "ru");

	const handleOptionSelect = (option: string): void => {
		setSelectedOption(option);
		setIsOpen(false);
		i18n.changeLanguage(option);
		localStorage.setItem("language", option);
		setIsRussian(option === "ru");
	};

	const handleClickOutside = (event: MouseEvent): void => {
		if (
			selectRef.current &&
			!selectRef.current.contains(event.target as Node)
		) {
			setIsOpen(false);
		}
	};

	useEffect(() => {
		document.addEventListener("click", handleClickOutside);
		return () => {
			document.removeEventListener("click", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		const changeFontByLanguage = () => {
			if (isRussian) {
				document.body.style.fontFamily = "'Raleway'";
				document.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach(element => {
					element.classList.add("ru-font");
					element.classList.remove("en-font");
				});
			} else {
				document.body.style.fontFamily = "'Poppins'";
				document.querySelectorAll("h1, h2, h3, h4, h5, h6").forEach(element => {
					element.classList.add("en-font");
					element.classList.remove("ru-font");
				});
			}
		};

		changeFontByLanguage();

		i18n.on("languageChanged", lng => {
			setIsRussian(lng === "ru");
			changeFontByLanguage();
		});

		return () => {
			i18n.off("languageChanged", changeFontByLanguage);
		};
	}, [isRussian, i18n]);

	useEffect(() => {
		const storedLanguage = localStorage.getItem("language");
		if (storedLanguage && storedLanguage !== selectedOption) {
			setSelectedOption(storedLanguage);
			i18n.changeLanguage(storedLanguage);
			setIsRussian(storedLanguage === "ru");
		}
	}, []);

	const toggleSelect = (): void => {
		setIsOpen(!isOpen);
	};

	const languageContainerClasses = clsx(
		classes.languageContainer,
		classes.wrapper,
		{ [classes.isOpen]: isOpen },
		{ [classes.en]: selectedOption === "en" },
		{ [classes.ru]: selectedOption === "ru" }
	);

	const titleClasses = clsx(classes.title, {
		[classes.russianTitle]: isRussian,
	});

	return (
		<div ref={selectRef} className={languageContainerClasses}>
			<div className={classes.options} onClick={toggleSelect}>
				<div className={titleClasses}>
					{selectedOption === "en" ? "English" : "Русский"}
				</div>
			</div>
			{isOpen && (
				<ul className={classes.list}>
					<li
						className={classes.option}
						onClick={() => handleOptionSelect("en")}
					>
						English
					</li>
					<li
						className={classes.option}
						onClick={() => handleOptionSelect("ru")}
					>
						Русский
					</li>
				</ul>
			)}
			<Icon name="arrow" size={8} />
		</div>
	);
};
